import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const Nav = styled(motion.nav)`
  position: fixed;
  top: 0; left: 0; right: 0;
  z-index: 50;
  padding: 0 2rem;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  transition: background 0.3s ease, border-color 0.3s ease;
  background: ${({ $scrolled }) => $scrolled ? 'rgba(10,10,10,0.92)' : 'transparent'};
  backdrop-filter: ${({ $scrolled }) => $scrolled ? 'blur(20px)' : 'none'};
  border-bottom: 1px solid ${({ $scrolled }) => $scrolled ? 'rgba(244,121,32,0.12)' : 'transparent'};

  @media (max-width: 600px) { padding: 0 1rem; }
`;

const LogoWrap = styled.a`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  cursor: pointer;
`;

const LogoImg = styled.img`
  height: 42px;
  width: auto;
  background: #fff;
  border-radius: 7px;
  padding: 4px 9px;

  @media (max-width: 480px) { height: 36px; }
`;

/* ── Desktop nav links — hidden below 900px ── */
const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  flex: 1;
  justify-content: center;

  @media (max-width: 1100px) { gap: 1.25rem; }
  @media (max-width: 900px)  { display: none; }
`;

const NavLink = styled.a`
  color: #D0D0D0;
  font-size: 0.8rem;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  white-space: nowrap;
  transition: color 0.2s ease;
  cursor: pointer;
  &:hover { color: #F47920; }
`;

/* ── Right side actions ── */
const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-shrink: 0;
`;

const EmpleoBtn = styled.a`
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #bbb;
  border: 1px solid #3A3A3A;
  padding: 0.45rem 1rem;
  border-radius: 7px;
  white-space: nowrap;
  cursor: pointer;
  transition: color 0.2s ease, border-color 0.2s ease;
  &:hover { color: #F47920; border-color: #F47920; }

  @media (max-width: 900px) { display: none; }
`;

/* Visible >900px */
const CTABtn = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  background: #F47920;
  color: #fff;
  padding: 0.5rem 1.25rem;
  border-radius: 7px;
  font-weight: 700;
  font-size: 0.8rem;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.2s ease;
  &:hover { background: #FF8C3A; }

  @media (max-width: 900px) { display: none; }
`;

/* Hamburger — visible <900px */
const HamburgerBtn = styled.button`
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  background: none;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  flex-shrink: 0;

  @media (max-width: 900px) { display: flex; }
`;

const Bar = styled.span`
  display: block;
  width: 22px;
  height: 2px;
  background: #fff;
  border-radius: 2px;
  transition: transform 0.25s ease, opacity 0.25s ease;
`;

/* ── Mobile menu ── */
const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 72px; left: 0; right: 0;
  background: rgba(8, 8, 8, 0.97);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid #252525;
  padding: 1.75rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0;
  z-index: 49;
`;

const MobileLink = styled.a`
  font-size: 0.95rem;
  color: #ccc;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 0.9rem 0;
  border-bottom: 1px solid #161616;
  transition: color 0.2s ease;
  &:hover { color: #F47920; }
  &:last-of-type { border-bottom: none; }
`;

const MobileDivider = styled.div`
  height: 1px;
  background: #161616;
  margin: 0.5rem 0;
`;

const MobileEmpleoLink = styled.a`
  font-size: 0.95rem;
  color: #F47920;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 0.9rem 0;
  border-bottom: 1px solid #161616;
  transition: opacity 0.2s ease;
  &:hover { opacity: 0.8; }
`;

const MobileCTA = styled.a`
  display: flex;
  justify-content: center;
  background: #F47920;
  color: white;
  padding: 0.85rem 2rem;
  border-radius: 9px;
  font-weight: 700;
  font-size: 0.9rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-top: 1rem;
  transition: background 0.2s ease;
  &:hover { background: #FF8C3A; }
`;

const LINKS = [
  { label: 'Nosotros',  href: '#nosotros'  },
  { label: 'Proceso',   href: '#proceso'   },
  { label: 'Productos', href: '#productos' },
  { label: 'Contacto',  href: '#contacto'  },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen]         = useState(false);
  const { pathname } = useLocation();
  const p = pathname === '/' ? '' : '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const close = () => setOpen(false);

  return (
    <>
      <Nav
        $scrolled={scrolled}
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.55, ease: 'easeOut' }}
      >
        <LogoWrap href={`${p}#inicio`}>
          <LogoImg src="/img/logo/logo.jpeg" alt="Carnes Fereima" />
        </LogoWrap>

        <NavLinks>
          {LINKS.map(l => (
            <NavLink key={l.label} href={`${p}${l.href}`}>{l.label}</NavLink>
          ))}
        </NavLinks>

        <Actions>
          <EmpleoBtn href="/empleo" target="_blank" rel="noopener noreferrer">
            Empleos Fereima
          </EmpleoBtn>

          <CTABtn href={`${p}#contacto`} whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}>
            Contáctanos
          </CTABtn>

          <HamburgerBtn onClick={() => setOpen(o => !o)} aria-label="Menú">
            <Bar style={{ transform: open ? 'rotate(45deg) translateY(7px)' : 'none' }} />
            <Bar style={{ opacity: open ? 0 : 1 }} />
            <Bar style={{ transform: open ? 'rotate(-45deg) translateY(-7px)' : 'none' }} />
          </HamburgerBtn>
        </Actions>
      </Nav>

      <AnimatePresence>
        {open && (
          <MobileMenu
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
          >
            {LINKS.map(l => (
              <MobileLink key={l.label} href={`${p}${l.href}`} onClick={close}>{l.label}</MobileLink>
            ))}
            <MobileDivider />
            <MobileEmpleoLink href="/empleo" target="_blank" rel="noopener noreferrer" onClick={close}>
              Empleos Fereima
            </MobileEmpleoLink>
            <MobileCTA href={`${p}#contacto`} onClick={close}>Contáctanos</MobileCTA>
          </MobileMenu>
        )}
      </AnimatePresence>
    </>
  );
}
